import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { MasterDataItem, MasterDataKind } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { etichettaAnagrafica } from '../components/SelettoreAnagrafica'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Le anagrafiche di riferimento (master data): centri di costo, unità
 * organizzative, sedi. Una tabella ad albero per tipo, il modulo in linea,
 * l'importazione da testo. Una voce in uso non si toglie: si disattiva, così
 * asset e contratti che la citano restano leggibili.
 */
const TIPI: { kind: MasterDataKind; label: () => string; descrizione: () => string; gerarchica: boolean }[] = [
  { kind: 'cost_center', label: () => t('Centri di costo'), descrizione: () => t('Definisce i centri di costo a cui imputare le spese di contratti e asset'), gerarchica: false },
  { kind: 'org_unit', label: () => t('Unità organizzative'), descrizione: () => t('Definisce direzioni, reparti e team per identificare le unità responsabili e utilizzatrici di contratti e asset'), gerarchica: true },
  { kind: 'location', label: () => t('Sedi'), descrizione: () => t('Definisce la collocazione fisica degli asset per sede, edificio, piano e sala'), gerarchica: true },
]

const ATTRIBUTI: Record<MasterDataKind, { key: string; label: () => string }[]> = {
  location: [
    { key: 'address', label: () => t('Indirizzo') }, { key: 'city', label: () => t('Città') }, { key: 'postal_code', label: () => t('CAP') },
    { key: 'country', label: () => t('Paese') }, { key: 'timezone', label: () => t('Fuso orario') }, { key: 'phone', label: () => t('Telefono') },
  ],
  org_unit: [{ key: 'manager', label: () => t('Responsabile') }, { key: 'email', label: () => t('Email') }],
  cost_center: [{ key: 'owner', label: () => t('Responsabile') }, { key: 'email', label: () => t('Email') }, { key: 'budget_year', label: () => t('Budget annuo') }],
}

type Form = { code: string; name: string; description: string; parent_id: string; active: boolean; attributes: Record<string, string> }
const VUOTO: Form = { code: '', name: '', description: '', parent_id: '', active: true, attributes: {} }

export default function MasterData() {
  const { principal } = useAuth()
  const puoModificare = principal?.roles.some((r) => ['tenant_admin', 'provider_admin', 'operator'].includes(r)) ?? false
  const [kind, setKind] = useState<MasterDataKind>('cost_center')
  const dati = useAsync(() => api.masterData(kind), [kind])
  const [aperto, setAperto] = useState<MasterDataItem | 'nuovo' | null>(null)
  const [form, setForm] = useState<Form>(VUOTO)
  const [importo, setImporto] = useState<string | null>(null)
  const [esitoImport, setEsitoImport] = useState<{ created: number; updated: number; errors: string[] } | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const tipo = TIPI.find((x) => x.kind === kind)!
  const voci = dati.data?.items ?? []

  const apri = (v?: MasterDataItem) => {
    setForm(v ? { code: v.code, name: v.name, description: v.description ?? '', parent_id: v.parent_id ?? '', active: v.active, attributes: { ...v.attributes } } : VUOTO)
    setAperto(v ?? 'nuovo')
    setErrore(null)
  }
  async function esegui(fn: () => Promise<unknown>) {
    setBusy(true)
    setErrore(null)
    try {
      await fn()
      await dati.reload()
      return true
    } catch (e) {
      setErrore(e)
      return false
    } finally {
      setBusy(false)
    }
  }
  async function salva(e: FormEvent) {
    e.preventDefault()
    const corpo = { ...form, code: form.code || undefined, parent_id: form.parent_id || null }
    const ok = await esegui(() => aperto === 'nuovo' || !aperto ? api.masterDataCrea(kind, corpo) : api.masterDataAggiorna(kind, aperto.id, corpo))
    if (ok) setAperto(null)
  }

  const colonneBase: Column<MasterDataItem>[] = [
    { key: 'code', header: t('Codice'), mono: true },
    {
      key: 'name',
      header: t('Nome'),
      value: (v) => [v.name, v.description].filter(Boolean).join(' '),
      render: (v) => (
        <div style={{ paddingLeft: (v.level ?? 0) * 18 }}>
          {(v.level ?? 0) > 0 && <span className="muted">└ </span>}{v.name}
          {v.description && <div className="muted eagle-piccolo">{v.description}</div>}
        </div>
      ),
    },
    ...ATTRIBUTI[kind].slice(0, 2).map((a): Column<MasterDataItem> => ({
      key: `attr_${a.key}`,
      header: a.label(),
      value: (v) => v.attributes[a.key],
      render: (v) => v.attributes[a.key] ?? <span className="muted">—</span>,
    })),
    {
      key: 'usage',
      header: t('In uso'),
      value: (v) => v.usage.assets + v.usage.contracts + (v.usage.medical ?? 0),
      render: (v) => (
        <>
          {v.usage.assets > 0 && <Link to={`/assets?${kind}_id=${v.id}`}>{t('{n} asset', { n: v.usage.assets })}</Link>}
          {v.usage.assets > 0 && v.usage.contracts > 0 && ' · '}
          {v.usage.contracts > 0 && <Link to={`/contracts?${kind}_id=${v.id}`}>{t('{n} contratti', { n: v.usage.contracts })}</Link>}
          {(v.usage.medical ?? 0) > 0 && (
            <>{v.usage.assets + v.usage.contracts > 0 && ' · '}{t('{n} elettromedicali', { n: v.usage.medical ?? 0 })}</>
          )}
          {v.usage.assets === 0 && v.usage.contracts === 0 && !v.usage.medical && <span className="muted">—</span>}
        </>
      ),
    },
    {
      key: 'active',
      header: t('Stato'),
      value: (v) => (v.active ? t('attiva') : t('disattivata')),
      render: (v) => (v.active ? <span className="badge badge--ok">{t('attiva')}</span> : <span className="badge badge--muted">{t('disattivata')}</span>),
    },
    ...(puoModificare
      ? [
          {
            key: 'azioni',
            header: '',
            sort: false,
            searchable: false,
            render: (v: MasterDataItem) => (
              <span style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="secondary" onClick={() => apri(v)} disabled={busy}>{t('Modifica')}</button>
                {v.usage.assets + v.usage.contracts + (v.usage.medical ?? 0) === 0 ? (
                  <button type="button" className="secondary" disabled={busy}
                          onClick={() => { if (window.confirm(t('Togliere «{n}»?', { n: v.name }))) void esegui(() => api.masterDataElimina(v.id)) }}>{t('Togli')}</button>
                ) : (
                  <button type="button" className="secondary" disabled={busy} title={t('In uso: si disattiva, non si toglie')}
                          onClick={() => void esegui(() => api.masterDataAggiorna(kind, v.id, { code: v.code, name: v.name, description: v.description, parent_id: v.parent_id, active: !v.active, attributes: v.attributes }))}>
                    {v.active ? t('Disattiva') : t('Riattiva')}
                  </button>
                )}
              </span>
            ),
          },
        ]
      : []),
  ]
  // Inactive entries stay listed but dimmed, as before.
  const colonne: Column<MasterDataItem>[] = colonneBase.map((col) => ({
    ...col,
    render: (v: MasterDataItem) => {
      const cella = col.render ? col.render(v) : (col.value?.(v) ?? (v as unknown as Record<string, string>)[col.key] ?? '—')
      return v.active ? cella : <div style={{ opacity: 0.7 }}>{cella}</div>
    },
  }))

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Anagrafiche')}</h1>
          <p className="page__subtitle">{t('Definisce le anagrafiche di riferimento della piattaforma, come centri di costo, unità organizzative e sedi. Contratti e asset utilizzano questi valori.')}</p>
        </div>
      </header>
      <div className="segmented" role="tablist" aria-label={t('Anagrafica')}>
        {TIPI.map((x) => (
          <button key={x.kind} type="button" role="tab" aria-selected={kind === x.kind} className={kind === x.kind ? 'segmented__on' : ''}
                  onClick={() => { setKind(x.kind); setAperto(null); setImporto(null); setEsitoImport(null) }}>{x.label()}</button>
        ))}
      </div>
      <ErrorBanner error={errore ?? dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : (
        <Card title={`${tipo.label()} (${voci.length})`}
              actions={puoModificare && !aperto && importo === null ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => apri()}>{t('Nuova voce')}</button>
                  <button type="button" className="secondary" onClick={() => { setImporto(''); setEsitoImport(null) }}>{t('Importa da testo')}</button>
                </div>
              ) : undefined}>
          <p className="muted" style={{ marginTop: 0 }}>{tipo.descrizione()}</p>

          {aperto && (
            <form onSubmit={salva} className="eagle-form">
              <div className="eagle-report__riga">
                <label className="field"><span className="field__label">{t('Codice')}</span>
                  <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder={t('vuoto = il nome')} className="mono" /></label>
                <label className="field"><span className="field__label">{t('Nome')}</span>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
              </div>
              {tipo.gerarchica && (
                <label className="field" style={{ maxWidth: 420 }}><span className="field__label">{t('Sta sotto')}</span>
                  <select value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}>
                    <option value="">{t('nessuna (primo livello)')}</option>
                    {voci.filter((v) => aperto === 'nuovo' || v.id !== aperto.id).map((v) => (
                      <option key={v.id} value={v.id}>{'  '.repeat(v.level ?? 0)}{v.name}</option>
                    ))}
                  </select></label>
              )}
              <label className="field"><span className="field__label">{t('Descrizione')}</span>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
              <div className="eagle-report__riga">
                {ATTRIBUTI[kind].map((a) => (
                  <label key={a.key} className="field"><span className="field__label">{a.label()}</span>
                    <input value={form.attributes[a.key] ?? ''} onChange={(e) => setForm({ ...form, attributes: { ...form.attributes, [a.key]: e.target.value } })} /></label>
                ))}
              </div>
              <label className="eagle-interruttore"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />{' '}{t('Attiva')}</label>
              <div className="eagle-form__azioni">
                <button type="submit" disabled={busy}>{busy ? t('Salvo…') : t('Salva')}</button>
                <button type="button" className="secondary" onClick={() => setAperto(null)} disabled={busy}>{t('Annulla')}</button>
              </div>
            </form>
          )}

          {importo !== null && (
            <div className="eagle-form">
              <label className="field"><span className="field__label">{t('Una voce per riga: codice; nome; codice del genitore (facoltativo)')}</span>
                <textarea rows={8} value={importo} onChange={(e) => setImporto(e.target.value)} className="mono"
                          placeholder={tipo.gerarchica ? 'MI;Milano\nMI-01;Milano, sede via Roma;MI' : 'CC100;Direzione IT\nCC200;Amministrazione'} /></label>
              <div className="eagle-form__azioni">
                <button type="button" disabled={busy || !importo.trim()} onClick={() => void esegui(async () => { setEsitoImport(await api.masterDataImporta(kind, importo)) })}>{busy ? t('Importo…') : t('Importa')}</button>
                <button type="button" className="secondary" onClick={() => { setImporto(null); setEsitoImport(null) }} disabled={busy}>{t('Chiudi')}</button>
              </div>
              {esitoImport && (
                <p className="muted" style={{ margin: 0 }}>
                  {t('{a} create, {b} aggiornate', { a: esitoImport.created, b: esitoImport.updated })}
                  {esitoImport.errors.length > 0 && <span className="eagle-motivo"> · {esitoImport.errors.join(' · ')}</span>}
                </p>
              )}
            </div>
          )}

          {voci.length === 0 ? (
            <EmptyState title={t('Nessuna voce ancora')} hint={t('Aggiungi la prima voce, o importa l’elenco da testo.')} />
          ) : (
            // No initial sort: the server's order is the tree's (parents before children).
            <DataTable
              rows={voci}
              columns={colonne}
              rowKey={(v) => v.id}
              conteggio={{ nome: t('voci') }}
            />
          )}
        </Card>
      )}
    </>
  )
}

export { etichettaAnagrafica }
